import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { SEO } from '../components/SEO';
import { ArticleCards } from '../components/ArticleCards';
import { RandomProducts } from '../components/RandomProducts';
import { ProductReviews } from '../components/ProductReviews';
import {
  ArrowLeft, ExternalLink, Star, Package, Tag, Loader2, Flame, ShoppingBag,
  ChevronRight, Clock, FileText, Lightbulb
} from 'lucide-react';

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/products/${id}`)
      .then(res => setProduct(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!product) return <div className="text-center py-20 text-muted-foreground">Produkt nie znaleziony</div>;

  const img = product.image_local_path ? `${BACKEND}${product.image_local_path}` : product.image_url;
  const buyUrl = product.affiliate_url || product.url;
  const discount = product.original_price && product.deal_price && product.original_price > product.deal_price
    ? Math.round((1 - product.deal_price / product.original_price) * 100)
    : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="product-detail-page">
      <SEO title={product.title} description={product.description?.slice(0, 160)} image={img} type="product" />

      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-5" data-testid="breadcrumb">
        <Link to="/" className="hover:text-white transition-colors">Znajdownik</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/produkty" className="hover:text-white transition-colors">Produkty</Link>
        {product.category && (
          <>
            <ChevronRight className="w-3 h-3" />
            <span className="text-primary/80">{product.category}</span>
          </>
        )}
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ═══ MAIN CONTENT ═══ */}
        <div className="lg:col-span-8 space-y-6">

          {/* ── Product Card (image + info + CTA) ── */}
          <div className="bg-card border border-border/40 overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {/* Image */}
              <div className="aspect-square sm:aspect-auto sm:h-full bg-secondary overflow-hidden relative">
                {img ? (
                  <img src={img} alt={product.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center min-h-[250px]"><Package className="w-16 h-16 text-muted-foreground/20" /></div>
                )}
                {discount && (
                  <div className="absolute top-3 left-3 bg-destructive text-destructive-foreground px-2.5 py-1 font-heading font-bold text-sm">
                    -{discount}%
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-green-600 text-white text-[10px] px-2 py-0.5 flex items-center gap-1">
                  <Star className="w-2.5 h-2.5" /> Recenzja eksperta
                </div>
              </div>

              {/* Product info */}
              <div className="p-5 sm:p-6 flex flex-col justify-between">
                <div>
                  <h1 className="font-heading text-xl sm:text-2xl font-extrabold tracking-tight text-white mb-3 leading-tight">
                    {product.title}
                  </h1>

                  {product.category && (
                    <div className="flex items-center gap-1 text-xs text-primary/80 mb-4">
                      <Tag className="w-3 h-3" /> {product.category}
                    </div>
                  )}

                  {/* Price block */}
                  <div className="bg-background border border-border/40 p-4 mb-4">
                    <div className="flex items-baseline gap-3">
                      {product.deal_price && (
                        <span className="price-tag text-3xl sm:text-4xl text-primary">{product.deal_price} zł</span>
                      )}
                      {product.original_price && product.deal_price && product.original_price > product.deal_price && (
                        <span className="text-base text-muted-foreground line-through">{product.original_price} zł</span>
                      )}
                    </div>
                    {discount && (
                      <p className="text-xs text-green-400 mt-1">Oszczędzasz {(product.original_price - product.deal_price).toFixed(0)} zł ({discount}% taniej)</p>
                    )}
                  </div>
                </div>

                {/* CTA */}
                {buyUrl && (
                  <a href={buyUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 font-heading font-bold uppercase tracking-wider text-base hover:bg-primary/90 transition-all active:scale-[0.98] w-full"
                    data-testid="product-buy-btn">
                    <ShoppingBag className="w-5 h-5" /> Przejdź do sklepu
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* ── SEO Description (main content area) ── */}
          {product.seo_description && (
            <div className="bg-card border border-border/40 overflow-hidden" data-testid="seo-description">
              <div className="border-b border-border/30 px-6 py-3 bg-secondary/30">
                <h2 className="font-heading text-base font-bold text-white uppercase tracking-wider flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" /> Opis produktu
                </h2>
              </div>
              <div className="p-6 seo-content">
                <div dangerouslySetInnerHTML={{ __html: product.seo_description }} />
              </div>
            </div>
          )}

          {/* ── Random products ── */}
          <RandomProducts count={3} excludeId={product?.id} />

          {/* ── Reviews ── */}
          <ProductReviews productId={product.id} />

          {/* ── Related articles ── */}
          <ArticleCards
            title="Powiązane artykuły"
            context={`${product.title} ${product.category || ''}`}
            limit={4}
          />

          {/* ── Recommended products (bottom grid) ── */}
          {(product.recommended_products || []).length > 0 && (
            <div className="mt-2" data-testid="recommended-products">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                  <Flame className="w-5 h-5 text-primary" /> Polecane produkty
                </h2>
                <Link to="/produkty" className="text-xs text-primary hover:text-primary/80 font-heading uppercase tracking-wider flex items-center gap-1">
                  Wszystkie <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {product.recommended_products.slice(0, 8).map(rp => {
                  const rpImg = rp.image_local_path ? `${BACKEND}${rp.image_local_path}` : rp.image_url;
                  return (
                    <Link key={rp.id} to={`/produkt/${rp.id}`} className="bg-card border border-border/40 hover:border-primary/30 transition-colors group overflow-hidden" data-testid={`rec-product-${rp.id}`}>
                      <div className="h-28 bg-secondary overflow-hidden">
                        {rpImg ? (
                          <img src={rpImg} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-muted-foreground/20" /></div>
                        )}
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs text-white group-hover:text-primary transition-colors line-clamp-2 font-medium leading-tight">{rp.title}</p>
                        {rp.deal_price && <p className="price-tag text-sm text-primary mt-1">{rp.deal_price} zł</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ═══ SIDEBAR ═══ */}
        <aside className="lg:col-span-4" data-testid="product-sidebar">
          <div className="lg:sticky lg:top-20 space-y-4">

            {/* CTA sticky */}
            {buyUrl && (
              <div className="bg-card border border-primary/30 p-4">
                <div className="flex items-baseline gap-2 mb-2">
                  {product.deal_price && <span className="price-tag text-2xl text-primary">{product.deal_price} zł</span>}
                  {product.original_price && product.deal_price && product.original_price > product.deal_price && (
                    <span className="text-sm text-muted-foreground line-through">{product.original_price} zł</span>
                  )}
                </div>
                <a href={buyUrl} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 font-heading font-bold uppercase tracking-wider text-sm hover:bg-primary/90 transition-colors w-full mb-2">
                  <ExternalLink className="w-4 h-4" /> Kup teraz
                </a>
                <p className="text-[10px] text-muted-foreground text-center">Link afiliacyjny - wspierasz portal</p>
              </div>
            )}

            {/* Random products */}
            <RandomProducts count={3} excludeId={product?.id} className="mb-4" vertical />

            {/* Sidebar deals */}
            <div className="bg-card border border-border/40 p-4" data-testid="sidebar-deals">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-white mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4 text-primary" /> Najnowsze okazje
              </h3>
              <div className="space-y-3">
                {(product.sidebar_deals || []).slice(0, 6).map(deal => {
                  const dImg = deal.image_local_path ? `${BACKEND}${deal.image_local_path}` : deal.image_url;
                  return (
                    <Link key={deal.id} to={`/okazja/${deal.slug || deal.id}`} className="flex gap-3 group">
                      {dImg && (
                        <div className="w-14 h-14 bg-secondary flex-shrink-0 overflow-hidden">
                          <img src={dImg} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white group-hover:text-primary transition-colors line-clamp-2 font-medium">{deal.title}</p>
                        {deal.deal_price && <p className="price-tag text-sm text-primary mt-0.5">{deal.deal_price} zł</p>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Similar products */}
            {(product.related_products || []).length > 0 && (
              <div className="bg-card border border-border/40 p-4" data-testid="sidebar-related">
                <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-white mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4 text-primary" /> Podobne produkty
                </h3>
                <div className="space-y-3">
                  {product.related_products.map(rp => {
                    const rpImg = rp.image_local_path ? `${BACKEND}${rp.image_local_path}` : rp.image_url;
                    return (
                      <Link key={rp.id} to={`/produkt/${rp.id}`} className="flex gap-3 group">
                        {rpImg && (
                          <div className="w-14 h-14 bg-secondary flex-shrink-0 overflow-hidden">
                            <img src={rpImg} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white group-hover:text-primary transition-colors line-clamp-2 font-medium">{rp.title}</p>
                          {rp.deal_price && <p className="price-tag text-sm text-primary mt-0.5">{rp.deal_price} zł</p>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Wachalski promo */}
            <div className="bg-card border border-border/40 p-4 text-center">
              <Lightbulb className="w-6 h-6 text-zorki mx-auto mb-1" />
              <p className="font-heading text-sm font-bold text-white mb-1">Szukasz czegoś innego?</p>
              <p className="text-xs text-muted-foreground mb-3">Wąchalski AI znajdzie to za Ciebie</p>
              <Link to="/wachalski" className="inline-block bg-secondary text-secondary-foreground px-4 py-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:bg-secondary/80 transition-colors">
                Szukaj z AI
              </Link>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
